/**
 * Admin Cache Tab
 * Monitor and clear application caches grouped by source
 */

import { LoadingSpinner } from '@components/ui';
import { type CacheStat, type CacheType, useCacheStats } from '@hooks/useCacheStats';
import { RefreshCw } from 'lucide-react';
import { type ReactElement, useMemo, useState } from 'react';

export function CacheTab(): ReactElement {
  const { caches, quota, loading, error, refetch, clearSelected, clearAll, clearing } =
    useCacheStats();
  const [selectedCaches, setSelectedCaches] = useState<Set<CacheType>>(new Set());
  const [clearSuccess, setClearSuccess] = useState(false);

  const formatNumber = (num: number | null): string => {
    if (num === null) return 'N/A';
    return num.toLocaleString();
  };

  const cachesBySource = useMemo(() => {
    return (caches || []).reduce(
      (acc, cache) => {
        if (cache.source && acc[cache.source]) {
          acc[cache.source].push(cache);
        }
        return acc;
      },
      {
        'Odds API': [] as CacheStat[],
        BallDontLie: [] as CacheStat[],
        Internal: [] as CacheStat[],
      },
    );
  }, [caches]);

  const handleToggleCache = (id: CacheType): void => {
    const newSelected = new Set(selectedCaches);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedCaches(newSelected);
  };

  const handleSelectAll = (): void => {
    const cacheList = caches || [];
    if (selectedCaches.size === cacheList.length) {
      setSelectedCaches(new Set());
    } else {
      setSelectedCaches(new Set(cacheList.map((c) => c.id)));
    }
  };

  const handleClearSelected = async (): Promise<void> => {
    if (selectedCaches.size === 0) return;
    if (!confirm(`Clear ${selectedCaches.size} selected cache(s)?`)) return;

    const success = await clearSelected(Array.from(selectedCaches));
    if (success) {
      setClearSuccess(true);
      setSelectedCaches(new Set());
      setTimeout(() => setClearSuccess(false), 3000);
    }
  };

  const handleClearAll = async (): Promise<void> => {
    if (!confirm('Are you sure you want to clear ALL caches?')) return;

    const success = await clearAll();
    if (success) {
      setClearSuccess(true);
      setSelectedCaches(new Set());
      setTimeout(() => setClearSuccess(false), 3000);
    }
  };

  return (
    <div className="bg-theme-surface rounded-lg p-6 border border-theme-border space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-theme-text">Cache Management</h2>
          <p className="text-sm text-theme-text-tertiary">
            Monitor and clear application caches ({(caches || []).length} caches)
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={loading}
          className="px-4 py-2 bg-theme-surface-hover hover:bg-theme-border text-theme-text-secondary rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Quota Info */}
      {quota?.quotaRemaining !== null && (
        <div className="flex gap-4 p-3 bg-theme-bg rounded-lg">
          <div className="text-sm">
            <span className="text-theme-text-tertiary">Odds API Quota: </span>
            <span className="font-medium text-theme-success">
              {formatNumber(quota?.quotaRemaining ?? null)}
            </span>
            <span className="text-theme-text-tertiary"> remaining</span>
          </div>
          {quota?.quotaUsed !== null && (
            <div className="text-sm">
              <span className="text-theme-text-tertiary">Used: </span>
              <span className="font-medium text-theme-text">
                {formatNumber(quota?.quotaUsed ?? null)}
              </span>
            </div>
          )}
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner />
        </div>
      )}

      {error && (
        <div className="bg-theme-danger/10 border border-theme-danger/20 rounded-lg p-4">
          <p className="text-sm text-theme-danger">{error}</p>
        </div>
      )}

      {!loading && !error && (caches || []).length > 0 && (
        <>
          {/* Action Buttons */}
          <div className="flex items-center gap-3 pb-2 border-b border-theme-border">
            <button
              type="button"
              onClick={handleSelectAll}
              className="text-sm text-theme-primary hover:text-theme-primary-hover"
            >
              {selectedCaches.size === (caches || []).length ? 'Deselect All' : 'Select All'}
            </button>
            <button
              type="button"
              onClick={handleClearSelected}
              disabled={selectedCaches.size === 0 || clearing}
              className="px-3 py-1.5 text-sm bg-theme-warning hover:bg-theme-warning/80 text-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              {clearing ? <LoadingSpinner /> : null}
              Clear Selected ({selectedCaches.size})
            </button>
            <button
              type="button"
              onClick={handleClearAll}
              disabled={clearing}
              className="px-3 py-1.5 text-sm bg-theme-danger hover:bg-theme-danger/80 text-white rounded transition-colors disabled:opacity-50 flex items-center gap-1"
            >
              {clearing ? <LoadingSpinner /> : null}
              Clear All
            </button>
            {clearSuccess && <span className="text-sm text-theme-success">Cleared!</span>}
          </div>

          {/* Cache Table by Source */}
          {(['Odds API', 'BallDontLie', 'Internal'] as const).map((source) => {
            const sourceCaches = cachesBySource[source];
            if (sourceCaches.length === 0) return null;

            return (
              <div key={source} className="space-y-2">
                <h3 className="text-sm font-semibold text-theme-text-secondary uppercase tracking-wide">
                  {source}
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-theme-border text-left">
                        <th className="py-2 px-2 w-8"></th>
                        <th className="py-2 px-2">Cache</th>
                        <th className="py-2 px-2 text-right">TTL</th>
                        <th className="py-2 px-2 text-right">Entries</th>
                        <th className="py-2 px-2 text-right">Hit Rate</th>
                        <th className="py-2 px-2 text-right">Hits</th>
                        <th className="py-2 px-2 text-right">Misses</th>
                        <th className="py-2 px-2 text-right">Last Activity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sourceCaches.map((cache) => (
                        <tr
                          key={cache.id}
                          className={`border-b border-theme-border/50 hover:bg-theme-bg/50 ${
                            selectedCaches.has(cache.id) ? 'bg-theme-primary/10' : ''
                          }`}
                        >
                          <td className="py-2 px-2">
                            <input
                              type="checkbox"
                              checked={selectedCaches.has(cache.id)}
                              onChange={() => handleToggleCache(cache.id)}
                              className="rounded border-theme-border text-theme-primary focus:ring-theme-primary"
                            />
                          </td>
                          <td className="py-2 px-2">
                            <div>
                              <div className="font-medium text-theme-text">{cache.name}</div>
                              <div className="text-xs text-theme-text-tertiary">
                                {cache.description}
                              </div>
                            </div>
                          </td>
                          <td className="py-2 px-2 text-right text-theme-text-secondary">
                            {cache.ttlDisplay}
                          </td>
                          <td className="py-2 px-2 text-right font-mono text-theme-text">
                            {cache.entries}
                          </td>
                          <td className="py-2 px-2 text-right">
                            <span
                              className={`font-medium ${
                                cache.hitRate >= 80
                                  ? 'text-theme-success'
                                  : cache.hitRate >= 50
                                    ? 'text-theme-warning'
                                    : 'text-theme-text-secondary'
                              }`}
                            >
                              {cache.hits + cache.misses > 0 ? `${cache.hitRate}%` : '-'}
                            </span>
                          </td>
                          <td className="py-2 px-2 text-right font-mono text-theme-success">
                            {cache.hits}
                          </td>
                          <td className="py-2 px-2 text-right font-mono text-theme-text-secondary">
                            {cache.misses}
                          </td>
                          <td className="py-2 px-2 text-right text-xs text-theme-text-tertiary">
                            {cache.lastUpdated
                              ? new Date(cache.lastUpdated).toLocaleTimeString()
                              : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
